import { useState, useEffect, useCallback } from 'react';
import type { FormEvent, ChangeEvent } from 'react';
import { TrashIcon, PencilIcon, LockClosedIcon } from '@heroicons/react/24/solid';
import { apiUrl, authFetch, authService, assistantApi } from '../utils/api';
import type { AssistantResponseDto } from '../types/assistantCommunity';

const EMPTY_TEACHER_ID = '00000000-0000-0000-0000-000000000000';

interface TeacherOption {
  id: string;
  fullName: string;
}

interface AssistantFormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phoneNumber: string;
  teacherId: string;
  active: boolean;
}

const AssistantsManagement = () => {
  const [assistants, setAssistants] = useState<AssistantResponseDto[]>([]);
  const [teachers, setTeachers] = useState<TeacherOption[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [editingAssistant, setEditingAssistant] = useState<AssistantResponseDto | null>(null);
  const [assistantToDelete, setAssistantToDelete] = useState<AssistantResponseDto | null>(null);
  const [newPassword, setNewPassword] = useState('');

  const [formData, setFormData] = useState<AssistantFormData>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phoneNumber: '',
    teacherId: '',
    active: true,
  });

  const fetchTeachers = async () => {
    try {
      const response = await authFetch(apiUrl('Teacher'), {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authService.getToken()}`,
        },
      });
      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        setTeachers(
          data.data.map((t: { id: string; fullName: string }) => ({
            id: t.id,
            fullName: t.fullName,
          }))
        );
      }
    } catch {
      console.error('Failed to load teachers for dropdown');
    }
  };

  const fetchAssistants = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await assistantApi.getAll(currentPage, pageSize);
      setAssistants(result.assistants || []);
      setTotalCount(result.totalCount ?? 0);
      setTotalPages(result.totalPages ?? 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'فشل تحميل المساعدين');
      setAssistants([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize]);

  useEffect(() => {
    fetchTeachers();
  }, []);

  useEffect(() => {
    fetchAssistants();
  }, [fetchAssistants]);

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      phoneNumber: '',
      teacherId: '',
      active: true,
    });
  };

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      if (editingAssistant) {
        const updatePayload: Record<string, unknown> = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phoneNumber: formData.phoneNumber || null,
          active: formData.active,
        };
        if (formData.teacherId) {
          updatePayload.teacherId = formData.teacherId;
        } else {
          updatePayload.teacherId = EMPTY_TEACHER_ID;
        }

        const result = await assistantApi.update(editingAssistant.id, updatePayload);
        if (!result.success) {
          throw new Error(String(result.message || result.data || 'فشل تحديث المساعد'));
        }
        showSuccess('تم تحديث المساعد بنجاح');
        setShowEditForm(false);
        setEditingAssistant(null);
      } else {
        const registerPayload = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          password: formData.password,
          phoneNumber: formData.phoneNumber || null,
          teacherId: formData.teacherId || null,
        };
        const result = await assistantApi.register(registerPayload);
        if (!result.success) {
          throw new Error(String(result.message || result.data || 'فشل إضافة المساعد'));
        }
        showSuccess('تم إضافة المساعد بنجاح');
        setShowAddForm(false);
      }
      resetForm();
      await fetchAssistants();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'حدث خطأ');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (assistant: AssistantResponseDto) => {
    setEditingAssistant(assistant);
    setFormData({
      firstName: assistant.firstName,
      lastName: assistant.lastName,
      email: assistant.email,
      password: '',
      phoneNumber: assistant.phoneNumber || '',
      teacherId: assistant.teacherId || '',
      active: assistant.active,
    });
    setShowEditForm(true);
  };

  const handleDelete = async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await assistantApi.delete(id);
      if (!result.success) {
        throw new Error(String(result.message || result.data || 'فشل حذف المساعد'));
      }
      showSuccess('تم حذف المساعد بنجاح');
      setAssistantToDelete(null);
      await fetchAssistants();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'حدث خطأ أثناء الحذف');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePassword = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingAssistant || !newPassword) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await assistantApi.changePassword(editingAssistant.id, { newPassword });
      if (!result.success) {
        throw new Error(String(result.message || result.data || 'فشل تغيير كلمة المرور'));
      }
      showSuccess('تم تغيير كلمة المرور بنجاح');
      setShowPasswordForm(false);
      setNewPassword('');
      setEditingAssistant(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'حدث خطأ');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredAssistants = assistants.filter(
    (a) =>
      a.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">إدارة حسابات المساعدين وربطهم بالمعلمين</p>
        <button
          onClick={() => {
            resetForm();
            setEditingAssistant(null);
            setShowAddForm(true);
          }}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
        >
          إضافة مساعد
        </button>
      </div>

      <div className="bg-white px-4 py-4 border-b border-gray-200 sm:px-6 rounded-t-lg shadow-sm mb-4">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="بحث بالاسم أو البريد..."
          className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
        />
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4 mb-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
      {successMessage && (
        <div className="rounded-md bg-green-50 p-4 mb-4">
          <p className="text-sm text-green-700">{successMessage}</p>
        </div>
      )}

      <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg bg-white">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">الاسم</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">البريد</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">المعلم</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">الحالة</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">إجراءات</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500">
                  جاري التحميل...
                </td>
              </tr>
            ) : filteredAssistants.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500">
                  لا يوجد مساعدون
                </td>
              </tr>
            ) : (
              filteredAssistants.map((assistant) => (
                <tr key={assistant.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {assistant.fullName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{assistant.email}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {assistant.teacherName || '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        assistant.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {assistant.active ? 'نشط' : 'غير نشط'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleEdit(assistant)}
                      className="text-indigo-600 hover:text-indigo-900 ml-3"
                      title="تعديل"
                    >
                      <PencilIcon className="h-5 w-5 inline" />
                    </button>
                    <button
                      onClick={() => {
                        setEditingAssistant(assistant);
                        setShowPasswordForm(true);
                      }}
                      className="text-yellow-600 hover:text-yellow-900 ml-3"
                      title="تغيير كلمة المرور"
                    >
                      <LockClosedIcon className="h-5 w-5 inline" />
                    </button>
                    <button
                      onClick={() => setAssistantToDelete(assistant)}
                      className="text-red-600 hover:text-red-900"
                      title="حذف"
                    >
                      <TrashIcon className="h-5 w-5 inline" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-gray-500">
            إجمالي {totalCount} مساعد — صفحة {currentPage} من {totalPages}
          </p>
          <div className="flex gap-2">
            <button
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => p - 1)}
              className="px-3 py-1 border rounded text-sm disabled:opacity-50"
            >
              السابق
            </button>
            <button
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
              className="px-3 py-1 border rounded text-sm disabled:opacity-50"
            >
              التالي
            </button>
          </div>
        </div>
      )}

      {(showAddForm || showEditForm) && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {showAddForm ? 'إضافة مساعد جديد' : 'تعديل المساعد'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">الاسم الأول</label>
                  <input
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">اسم العائلة</label>
                  <input
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">البريد الإلكتروني</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 text-sm"
                />
              </div>
              {showAddForm && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">كلمة المرور</label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 text-sm"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700">رقم الهاتف (اختياري)</label>
                <input
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">المعلم (اختياري)</label>
                <select
                  name="teacherId"
                  value={formData.teacherId}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 text-sm"
                >
                  <option value="">بدون معلم</option>
                  {teachers.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.fullName}
                    </option>
                  ))}
                </select>
              </div>
              {showEditForm && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="active"
                    id="assistant-active"
                    checked={formData.active}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                  />
                  <label htmlFor="assistant-active" className="mr-2 text-sm text-gray-900">
                    حساب نشط
                  </label>
                </div>
              )}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setShowEditForm(false);
                    setEditingAssistant(null);
                    resetForm();
                  }}
                  className="py-2 px-4 border border-gray-300 rounded-md text-sm text-gray-700"
                >
                  إلغاء
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="py-2 px-4 bg-indigo-600 text-white rounded-md text-sm disabled:opacity-50"
                >
                  {isLoading ? 'جاري الحفظ...' : showAddForm ? 'إضافة' : 'حفظ'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPasswordForm && editingAssistant && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              تغيير كلمة مرور {editingAssistant.fullName}
            </h3>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">كلمة المرور الجديدة</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 text-sm"
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordForm(false);
                    setNewPassword('');
                    setEditingAssistant(null);
                  }}
                  className="py-2 px-4 border border-gray-300 rounded-md text-sm"
                >
                  إلغاء
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="py-2 px-4 bg-indigo-600 text-white rounded-md text-sm disabled:opacity-50"
                >
                  {isLoading ? 'جاري الحفظ...' : 'تغيير'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {assistantToDelete && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium text-gray-900 mb-4">حذف المساعد</h3>
            <p className="text-sm text-gray-500 mb-6">
              هل أنت متأكد من حذف {assistantToDelete.fullName}؟ لا يمكن التراجع عن هذا الإجراء.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setAssistantToDelete(null)}
                className="py-2 px-4 border border-gray-300 rounded-md text-sm"
              >
                إلغاء
              </button>
              <button
                onClick={() => handleDelete(assistantToDelete.id)}
                disabled={isLoading}
                className="py-2 px-4 bg-red-600 text-white rounded-md text-sm disabled:opacity-50"
              >
                {isLoading ? 'جاري الحذف...' : 'حذف'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssistantsManagement;
